import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setPlayerType,
  selectCurrentChannel,
  selectPlayerType,
  selectChannels,
  selectSelectedCategory,
} from "@app/channels/channelsSlice";
import { getEpgList, getArchiveUrl } from "@server/requests";
import LOCAL_STORAGE from "@utils/localStorage";
import { useTranslation } from "react-i18next";
import useKeydown from "@hooks/useKeydown";

import CardEpg from "./CardEpg";
import SmallLoading from "../../common/SmallLoading";

import "../styles/EpgListWrapper.scss";
import ArrowButton from "../../common/ArrowButton";
import {
  setArchives,
  setCurrentArchive,
  selectCurrentArchive,
  setPaused,
} from "@app/player/playerSlice";
import { setPlayerPosition, setUrlArchive } from "@utils/util";

export default memo(function EpgListWrapper({
  control,
  setControl,
  setPipMode,
  setUrl,
  url,
  refUrlLive,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentChannel = useSelector(selectCurrentChannel);
  const playerType = useSelector(selectPlayerType);
  const currentArchive = useSelector(selectCurrentArchive);

  const [emptyList, setEmptyList] = useState(false);
  const [epgList, setEpgList] = useState([]);
  const [loading, setLoading] = useState(false);

  const channelCategories = useSelector(selectChannels);
  const selectedCategory = useSelector(selectSelectedCategory);

  const [active, setActive] = useState(0);
  const [start, setStart] = useState(0);
  const [currentEpgIndex, setCurrentEpgIndex] = useState(null);

  const currnetDate = useRef(new Date().getTime());

  useEffect(() => {
    if (currentChannel) {
      setActive(0);
      setStart(0);
      setCurrentEpgIndex(null);
      reqGetEpgList();
    }
  }, [currentChannel]);

  useEffect(() => {
    if (currentArchive && epgList.length > 0 && playerType === "archive") {
      let archiveIndex = epgList.findIndex(
        (item) =>
          item.start_ut === currentArchive.start_ut &&
          item.stop_ut === currentArchive.stop_ut
      );

      if (archiveIndex === -1) {
        archiveIndex = epgList.findIndex(
          (item) =>
            currentArchive.start_ut >= item.start_ut &&
            currentArchive.start_ut < item.stop_ut
        );
      }

      if (archiveIndex !== -1) {
        setActive(archiveIndex);
        setStart(Math.max(0, archiveIndex - 1));
      }
    }
  }, [currentArchive, playerType]);

  const reqGetEpgList = async () => {
    setLoading(true);
    currnetDate.current = new Date().getTime();

    try {
      const res = await getEpgList({
        date: "-1,1",
        id: currentChannel.id_epg,
      });
      const parsedRes = JSON.parse(res);
      const { message, error } = parsedRes;

      if (error) {
        setEmptyList(true);
      } else if (message.length === 0) {
        setEmptyList(true);
      } else {
        setEmptyList(false);

        const now = currnetDate.current;
        const currentLiveEpgIndex = message.findIndex(
          (e) => e.start_ut * 1000 < now && e.stop_ut * 1000 > now
        );

        if (currentLiveEpgIndex !== -1) {
          setCurrentEpgIndex(currentLiveEpgIndex);
        }

        setEpgList([...message]);
        dispatch(setArchives([...message]));

        let targetIndex = -1;

        if (currentArchive && playerType === "archive") {
          let archiveIndex = message.findIndex(
            (item) =>
              item.start_ut === currentArchive.start_ut &&
              item.stop_ut === currentArchive.stop_ut
          );

          if (archiveIndex === -1) {
            archiveIndex = message.findIndex(
              (item) =>
                currentArchive.start_ut >= item.start_ut &&
                currentArchive.start_ut < item.stop_ut
            );
          }

          if (archiveIndex !== -1) {
            targetIndex = archiveIndex;
          }
        }

        if (targetIndex === -1 && currentLiveEpgIndex !== -1) {
          targetIndex = currentLiveEpgIndex;
        }

        if (targetIndex !== -1) {
          setActive(targetIndex);
          setStart(Math.max(0, targetIndex - 1));
        }
      }
    } catch (e) {
      setEmptyList(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUp = () => {
    if (active === 0) return;

    setActive(active - 1);
    if (active - 1 > 0 && active - 1 < epgList.length - 2) setStart(start - 1);
  };

  const handleDown = () => {
    if (active === epgList.length - 1) return;

    setActive(active + 1);
    if (active > 0 && active < epgList.length - 2) setStart(start + 1);
  };

  const getArchiveUrlHandler = async ({ id, start, end, type }) => {
    try {
      const response = await getArchiveUrl({ id, start, end, type });
      const parsedResponse = JSON.parse(response);
      const { error, message } = parsedResponse;
      if (error) return null;
      return message.url;
    } catch (error) {
      console.log(error, "getArchiveUrlHandler error");
      return null;
    }
  };

  const handleClickEpg = useCallback(
    async (item) => {
      let type =
        item.start_ut * 1000 < currnetDate.current &&
        item.stop_ut * 1000 > currnetDate.current
          ? "now"
          : currnetDate.current < item.start_ut * 1000
            ? "future"
            : "past";

      if (type === "now" && playerType === "live") {
        setPipMode(false);
        setPlayerPosition();
        return;
      }

      if (type === "now" && playerType === "archive") {
        setUrl(currentChannel.url);
        dispatch(setPlayerType("live"));
        dispatch(setPaused(false));
        setPipMode(false);
        setPlayerPosition();
        return;
      }

      if (type === "past" && currentChannel?.has_archive) {
        const archiveUrl = await getArchiveUrlHandler({
          id: currentChannel.id,
          start: item.start_ut,
          end: item.stop_ut,
          type: "archive",
        });

        if (!archiveUrl) {
          return;
        }

        setUrl(archiveUrl);

        dispatch(setCurrentArchive(item));
        dispatch(setPaused(false));
        if (playerType === "live") refUrlLive.current = url;
        dispatch(setPlayerType("archive"));
        setPipMode(false);
        setPlayerPosition();
      }
    },
    [currentChannel, playerType]
  );

  useKeydown({
    isActive: control,

    up: handleUp,

    down: handleDown,

    left: () => {
      const selectedCategoryChannels =
        channelCategories[selectedCategory]?.total;
      if (selectedCategoryChannels && selectedCategoryChannels > 0) {
        setControl("channel");
      } else {
        setControl("category");
      }
    },

    ok: () => {
      handleClickEpg(epgList[active]);
    },
  });

  return (
    <div className="parent-epg_list">
      <img
        className="logo-currnet_channel"
        src={currentChannel?.image}
        alt=""
        onError={(e) => (e.target.src = LOCAL_STORAGE.LOGO.GET())}
      />

      {playerType !== "live" ? (
        <div className="rec_video">{t("REC")}</div>
      ) : null}

      <div className="epg-list-wrapper">
        {loading ? (
          <SmallLoading />
        ) : (
          <>
            {!emptyList && active !== 0 ? (
              <ArrowButton onClick={handleUp} type="up" />
            ) : null}

            {emptyList ? (
              <p className="empty">{t("List is empty")}</p>
            ) : (
              <div
                className="main-epg_list"
                onWheel={(e) => {
                  if (e.deltaY < 0) handleUp();
                  else handleDown();
                }}
              >
                {epgList.map((item, index) => {
                  let type =
                    item.start_ut * 1000 < currnetDate.current &&
                    item.stop_ut * 1000 > currnetDate.current
                      ? "now"
                      : currnetDate.current < item.start_ut * 1000
                        ? "future"
                        : "past";

                  const isPlaying =
                    currentArchive &&
                    ((item.start_ut === currentArchive.start_ut &&
                      item.stop_ut === currentArchive.stop_ut) ||
                      (currentArchive.start_ut >= item.start_ut &&
                        currentArchive.start_ut < item.stop_ut));

                  return index >= start && index < start + 3 ? (
                    <CardEpg
                      key={item.start}
                      item={item}
                      currentEpg={isPlaying}
                      nextEpg={currentEpgIndex + 1 == index}
                      isActive={index === active && control}
                      type={type}
                      hasArchive={currentChannel?.has_archive}
                      onClick={handleClickEpg}
                    />
                  ) : null;
                })}
              </div>
            )}
            {!emptyList && active !== epgList.length - 1 ? (
              <ArrowButton onClick={handleDown} type="down" />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
});
